/**
 * TanStack Query setup: one client per app, retry policy that does not retry
 * client errors, and the query keys used across pages (so mutations can
 * invalidate precisely).
 */
import { QueryClient } from '@tanstack/react-query';
import { HttpError } from './api.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: true,
        // 4xx answers (no access, not found, missing Microsoft 365 permission) will not change by retrying.
        retry: (failureCount, err) => !(err instanceof HttpError && err.status < 500) && failureCount < 2,
      },
      mutations: { retry: false },
    },
  });
}

export const qk = {
  plannings: ['plannings'] as const,
  agenda: (planningId: string | null, resourceUid: number | null) => ['agenda', planningId, resourceUid] as const,
  deadlines: (planningId: string, resourceUid: number) => ['deadlines', planningId, resourceUid] as const,
  projects: (planningId: string) => ['projects', planningId] as const,
  project: (planningId: string, uid: number) => ['project', planningId, uid] as const,
  sharepointFiles: (planningId: string, uid: number) => ['sp-files', planningId, uid] as const,
  sharepointSuggest: (path: string) => ['sp-suggest', path] as const,
  sharepointSearch: (q: string) => ['sp-search', q] as const,
  sharepointChildren: (driveId: string | null, itemId: string | null) => ['sp-children', driveId, itemId] as const,
  /** Keyed by the saved site URL: the server lists the libraries of the stored site. */
  sharepointLibraries: (siteUrl: string | null) => ['sp-libraries', siteUrl] as const,
  directory: (q: string) => ['directory', q] as const,
  adminUsers: ['admin-users'] as const,
  adminSettings: ['admin-settings'] as const,
  adminStatus: ['admin-status'] as const,
  adminGraph: ['admin-graph'] as const,
  directorySync: ['directory-sync'] as const,
  linkSuggestions: (planningId: string) => ['link-suggestions', planningId] as const,
  audit: (planningId: string) => ['audit', planningId] as const,
  files: (planningId: string) => ['files', planningId] as const,
};
